import { Fragment, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import { GKToggleableInput, Icon, IconEnum } from '@greenkub-fronts/shared/ui';
import {
  bdcIntro,
  bdcOutro1,
  bdcOutro2,
  bdcRetract,
  gekaLogoGreen_nobg_png,
} from '@greenkub-fronts/shared/ui';
import { formatCurrency } from '@greenkub-fronts/shared/utils';
import {
  Address,
  ArraySignatures,
  FixMe,
  Project,
} from '@greenkub-fronts/shared/interface';

import styles from './bdc.module.scss';
import mainStyles
  from '../../document-signature-container/document-signature-container.module.scss';
import templateStyles from '../base-template.module.scss';
import { AddressPdfBloc } from '../address/addressPdfBloc';
import { AddressShortPdfBloc } from '../address/addressShortPdfBloc';

export interface BdcProps {
  isSignature: boolean;
  signature: string;
  signatures: ArraySignatures;
  sign1Ref: FixMe;
  sign2Ref: FixMe;
  handleOpenModalSignHandler: (e: FixMe) => void;
  isKit: boolean;
  project: Project;
  itemPages: [[]];
  localDatetime: string;
  address: Address;
  updateAddress: (e: FixMe) => void;
  totalPrice: number;
  adjustment: number;
  updateAdjustment: (e: FixMe) => void;
  totalVatAdjusted: number;
  totalPriceWithVatAdjusted: number;
  updateComment: (e: FixMe) => void;
  comment: string;
  setComment: (e: FixMe) => void;
  retractation?: string[];
}

const Bdc = (props: BdcProps) => {
  const {
    isSignature = false,
    signature,
    signatures,
    sign1Ref,
    sign2Ref,
    handleOpenModalSignHandler,
    isKit,
    project,
    itemPages,
    localDatetime,
    address,
    updateAddress,
    totalPrice,
    adjustment,
    updateAdjustment,
    totalVatAdjusted,
    totalPriceWithVatAdjusted,
    updateComment,
    comment,
    setComment,
    retractation = [],
  } = props;

  // Fetch and store technical description images per model
  const [modelImages, setModelImages] = useState<Record<string, string[]>>({});
  const [loadingModels, setLoadingModels] = useState<Record<string, boolean>>({});
  const [errorModels, setErrorModels] = useState<Record<string, string | null>>({});

  // Fetch and store retractation images
  const [retractationImages, setRetractationImages] = useState<string[]>([]);
  const [loadingRetractation, setLoadingRetractation] = useState(false);
  const [errorRetractation, setErrorRetractation] = useState<string | null>(null);

  // Fetch and store outro images
  const [outroImages, setOutroImages] = useState<string[]>([]);
  const [loadingOutro, setLoadingOutro] = useState(false);
  const [errorOutro, setErrorOutro] = useState<string | null>(null);

  // Fetch and store CGV images
  const [cgvImages, setCgvImages] = useState<string[]>([]);
  const [loadingCgv, setLoadingCgv] = useState(false);
  const [errorCgv, setErrorCgv] = useState<string | null>(null);

  // Fetch and store bandeau image
  const [bandeauUrl, setBandeauUrl] = useState<string | null>(null);
  const [loadingBandeau, setLoadingBandeau] = useState(false);
  const [errorBandeau, setErrorBandeau] = useState<string | null>(null);

  const modelNames = useMemo(() => {
    if (!project?.configurations) return [] as string[];
    return Array.from(
      new Set(
        project.configurations
          .map((c: FixMe) => c?.chosenStudioList?.studio?.model?.name)
          .filter(Boolean)
      )
    ) as string[];
  }, [project?.configurations]);

  // Build fallback fragments from legacy descriptionGallery for a given model name
  const renderOldGalleryForModel = (modelName: string): JSX.Element[] => {
    const fragments: JSX.Element[] = [];
    if (!project?.configurations) return fragments;

    project.configurations.forEach((config: FixMe) => {
      const model = config?.chosenStudioList?.studio?.model;
      if (!model || model.name !== modelName) return;

      if (!model.productPage && model.descriptionGallery) {
        const configGallery = model.descriptionGallery as any;
        const keyStart = `${config.id}_${model.id}`;

        const pushImg = (keySuffix: string, src?: string, alt?: string) => {
          if (!src) return;
          fragments.push(
            <div
              key={`${keyStart}_${keySuffix}`}
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void']
              )}
            >
              <img className={styles['full-picture']} src={src} alt={alt || keySuffix} />
            </div>
          );
        };

        pushImg('main_picture', configGallery.main_picture, 'main_picture');
        pushImg('page_1', configGallery.page_1, 'page_1');
        pushImg('page_2', configGallery.page_2, 'page_2');
        pushImg('page_3', configGallery.page_3, 'page_3');
        pushImg('page_4', configGallery.page_4, 'page_4');
        pushImg('page_5', configGallery.page_5, 'page_5');
        pushImg('page_6', configGallery.page_6, 'page_6');
        pushImg(
          'geometry_dimensions',
          configGallery.geometry_dimensions,
          'geometry_dimensions'
        );
        pushImg('technical_cut', configGallery.technical_cut, 'technical_cut');
      }
    });

    return fragments;
  };

  useEffect(() => {
    if (!project?.configurations) return;

    if (modelNames.length === 0) return;

    modelNames.forEach((modelName) => {
      if (!modelName) return;
      if (modelImages[modelName] || loadingModels[modelName]) return;

      setLoadingModels((prev) => ({ ...prev, [modelName]: true }));
      fetch(
        `https://pim.greenkub.fr/model/${encodeURIComponent(modelName)}/descriptifs_techniques`
      )
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => {
          // If API signals an error, register it so we can fallback to legacy gallery
          if (data && typeof data === 'object' && 'error' in (data as any)) {
            const msg = (data as any).error || 'Unknown error';
            setErrorModels((prev) => ({ ...prev, [modelName]: String(msg) }));
            return;
          }
          // Accept either an array of strings or array of objects with a `url` field
          const toAbsolute = (u: string) =>
            /^https?:\/\//i.test(u)
              ? u
              : `https://pim.greenkub.fr${u.startsWith('/') ? u : `/${u}`}`;

          const urls: string[] = Array.isArray(data.images)
            ? data.images
                .map((d: unknown) => {
                  if (typeof d === 'string') return toAbsolute(d);
                  if (d && typeof d === 'object' && 'url' in (d as Record<string, unknown>)) {
                    const u = (d as Record<string, unknown>).url;
                    return typeof u === 'string' ? toAbsolute(u) : null;
                  }
                  return null;
                })
                .filter((u: string | null): u is string => Boolean(u))
            : [];

          setModelImages((prev) => ({ ...prev, [modelName]: urls }));
          setErrorModels((prev) => ({ ...prev, [modelName]: null }));
        })
        .catch((err: unknown) => {
          setErrorModels((prev) => ({
            ...prev,
            [modelName]: err instanceof Error ? err.message : 'Unknown error',
          }));
        })
        .finally(() => {
          setLoadingModels((prev) => ({ ...prev, [modelName]: false }));
        });
    });
  }, [modelNames]);

  // Fetch retractation images
  useEffect(() => {
    setLoadingRetractation(true);
    fetch('https://pim.greenkub.fr/retractation')
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // If API signals an error, register it so we can fallback to default
        if (data && typeof data === 'object' && 'error' in (data as any)) {
          const msg = (data as any).error || 'Unknown error';
          setErrorRetractation(String(msg));
          return;
        }
        // Accept either an array of strings or array of objects with a `url` field
        const toAbsolute = (u: string) =>
          /^https?:\/\//i.test(u)
            ? u
            : `https://pim.greenkub.fr${u.startsWith('/') ? u : `/${u}`}`;

        const urls: string[] = Array.isArray(data.images)
          ? data.images
              .map((d: unknown) => {
                if (typeof d === 'string') return toAbsolute(d);
                if (d && typeof d === 'object' && 'url' in (d as Record<string, unknown>)) {
                  const u = (d as Record<string, unknown>).url;
                  return typeof u === 'string' ? toAbsolute(u) : null;
                }
                return null;
              })
              .filter((u: string | null): u is string => Boolean(u))
          : [];

        setRetractationImages(urls);
        setErrorRetractation(null);
      })
      .catch((err: unknown) => {
        setErrorRetractation(
          err instanceof Error ? err.message : 'Unknown error'
        );
      })
      .finally(() => {
        setLoadingRetractation(false);
      });
  }, []);

  // Fetch outro images
  useEffect(() => {
    // Guard: only fetch if project is loaded and isKit is stable
    if (!project || typeof isKit !== 'boolean') return;

    setLoadingOutro(true);
    const outroType = isKit ? 'geka' : 'greenkub';
    fetch(`https://pim.greenkub.fr/type/${outroType}/outro`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Accept either an array of strings or array of objects with a `url` field
        const toAbsolute = (u: string) =>
          /^https?:\/\//i.test(u)
            ? u
            : `https://pim.greenkub.fr${u.startsWith('/') ? u : `/${u}`}`;

        const urls: string[] = Array.isArray(data.images)
          ? data.images
              .map((d: unknown) => {
                if (typeof d === 'string') return toAbsolute(d);
                if (d && typeof d === 'object' && 'url' in (d as Record<string, unknown>)) {
                  const u = (d as Record<string, unknown>).url;
                  return typeof u === 'string' ? toAbsolute(u) : null;
                }
                return null;
              })
              .filter((u: string | null): u is string => Boolean(u))
          : [];

        setOutroImages(urls);
        setErrorOutro(null);
      })
      .catch((err: unknown) => {
        setErrorOutro(
          err instanceof Error ? err.message : 'Unknown error'
        );
      })
      .finally(() => {
        setLoadingOutro(false);
      });
  }, [isKit, project]);

  // Fetch bandeau image (intro cover) from PIM depending on type (geka / greenkub)
  useEffect(() => {
    if (!project || typeof isKit !== 'boolean') return;

    setLoadingBandeau(true);
    const bandeauType = isKit ? 'geka' : 'greenkub';

    fetch(`https://pim.greenkub.fr/type/${bandeauType}/bandeau`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data && typeof data === 'object' && 'url' in (data as any)) {
          const rawUrl = (data as any).url as string;
          const toAbsolute = (u: string) =>
            /^https?:\/\//i.test(u)
              ? u
              : `https://pim.greenkub.fr${u.startsWith('/') ? u : `/${u}`}`;

          setBandeauUrl(toAbsolute(rawUrl));
          setErrorBandeau(null);
        } else {
          setErrorBandeau('Malformed bandeau response');
        }
      })
      .catch((err: unknown) => {
        setErrorBandeau(
          err instanceof Error ? err.message : 'Unknown error'
        );
      })
      .finally(() => {
        setLoadingBandeau(false);
      });
  }, [isKit, project]);

  // Fetch CGV images
  useEffect(() => {
    // Guard: only fetch if project is loaded and isKit is stable
    if (!project || typeof isKit !== 'boolean') return;

    setLoadingCgv(true);
    const cgvType = isKit ? 'geka' : 'greenkub';
    fetch(`https://pim.greenkub.fr/type/${cgvType}/cgv`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Accept either an array of strings or array of objects with a `url` field
        const toAbsolute = (u: string) =>
          /^https?:\/\//i.test(u)
            ? u
            : `https://pim.greenkub.fr${u.startsWith('/') ? u : `/${u}`}`;

        const urls: string[] = Array.isArray(data.images)
          ? data.images
              .map((d: unknown) => {
                if (typeof d === 'string') return toAbsolute(d);
                if (d && typeof d === 'object' && 'url' in (d as Record<string, unknown>)) {
                  const u = (d as Record<string, unknown>).url;
                  return typeof u === 'string' ? toAbsolute(u) : null;
                }
                return null;
              })
              .filter((u: string | null): u is string => Boolean(u))
          : [];

        setCgvImages(urls);
        setErrorCgv(null);
      })
      .catch((err: unknown) => {
        setErrorCgv(
          err instanceof Error ? err.message : 'Unknown error'
        );
      })
      .finally(() => {
        setLoadingCgv(false);
      });
  }, [isKit, project]);

  return (
    <div
      className={cx(
        mainStyles['LandingPage'],
        styles['Bdc'],
        mainStyles['pages-container'],
      )}
    >
      <div
        className={cx(
          mainStyles['LandingPage-page'],
          templateStyles['Template'],
          styles['page'],
          styles['void'],
          styles['page-1'],
        )}
      >
        <div className={styles['page-1-content']}>
          {/* LOGO */}
          <div className={styles['page-1-logo-container']}>
            {isKit ? (
              <Icon
                style={{ marginBottom: '-100px' }}
                name={IconEnum.GEKA_LOGO_GREEN_NO_BG}
                width={'230'}
              />
            ) : (
              <Icon name={IconEnum.LOGO} width={'230'} />
            )}
          </div>

          {/* TITRE */}
          {isKit ? (
            <h1 className={cx(styles['page-1-title'], 'mb30')}>
              Votre commande Geka
            </h1>
          ) : (
            <h1 className={cx(styles['page-1-title'], 'mb30')}>
              Votre commande Greenkub
            </h1>
          )}

          <div className={styles['page-1-infos']}>
            {project && (
              <>
                {/* INFO CLIENT */}
                <div className={styles['Bdc-client']}>
                  <AddressShortPdfBloc project={project} />
                  <p>N° du projet&nbsp;:{project.id}</p>
                  <p>Date&nbsp;: {localDatetime}</p>
                </div>
                {/* INFO EMPLOYE */}
                <div>
                  <p>
                    <b>Réalisé par&nbsp;: </b>
                  </p>
                  <p>
                    {project?.sales?.firstName} {project?.sales?.lastName}
                  </p>
                  <p>Email&nbsp;: {project?.sales?.email}</p>
                  <p>Téléphone&nbsp;: 09 74 59 41 72</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Intro bandeau image: loaded from PIM based on type (geka / greenkub) */}
        <img
          className={styles['page-1-cover']}
          src={
            bandeauUrl ||
            'https://dlsoyv6qz5ct59pz.public.blob.vercel-storage.com/assets/images/bandeau/illustration-intro-devis.webp'
          }
          alt="bdcIntro"
        />
      </div>

      {/* PAGE 2 et + */}
      {itemPages.map((itemPage: FixMe, pageIdx: number) => (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles[`page-${pageIdx + 2}`],
          )}
          key={pageIdx}
        >
          <div>
            {pageIdx === 0 && (
              <>
                {/* Titre */}
                <div className={templateStyles['Template-head']}>
                  {isKit ? (
                    <img
                      src={gekaLogoGreen_nobg_png}
                      style={{ marginTop: '-40px' }}
                      alt="Logo Greenkub"
                    />
                  ) : (
                    <Icon name={IconEnum.LOGO} width={'230'} />
                  )}
                  <h1>
                    Bon de commande <span>{project?.quotation}</span>
                  </h1>
                </div>

                {/* EN TETE */}
                <div className={styles['Bdc-project']}>
                  <ul className={styles['Bdc-greenkub']}>
                    <li>
                      Projet&nbsp;: <span>{project?.id}</span>
                    </li>
                    <li>
                      Date&nbsp;: <span>{localDatetime}</span>
                    </li>
                    {project && (
                      <li>
                        Designer d'Espace&nbsp;:{' '}
                        <span>
                          {project?.sales?.firstName} {project?.sales?.lastName}
                        </span>
                      </li>
                    )}
                    {project && (
                      <li>
                        Email&nbsp;: <span>{project?.sales?.email}</span>
                      </li>
                    )}
                    {project && (
                      <li>
                        Téléphone&nbsp;: <span>09 74 59 41 72</span>
                      </li>
                    )}
                  </ul>

                  {/* EN TETE CLIENT */}
                  <div className={styles['Bdc-client']}>
                    {project && (
                      <AddressPdfBloc project={project} />
                    )}
                  </div>
                </div>
              </>
            )}

            {/* DONNEES BDC  */}
            <div className={styles['Bdc-tables']}>
              {itemPage.length > 0 && (
                <table className={styles['Bdc-products']}>
                  <thead>
                  <tr>
                    <th>Désignation</th>
                    <th>Prix HT</th>
                    <th>Qté</th>
                    <th>Total HT</th>
                  </tr>
                  </thead>
                  <tbody>
                  {itemPage.map((item, idx) => (
                    <tr key={idx}>
                      <td>
                        <strong>{item.name}</strong>
                        {item.description && <p>{item.description}</p>}
                        {item.description2 && <p>{item.description2}</p>}
                        {item.description3 && <p>{item.description3}</p>}
                      </td>
                      <td>{formatCurrency(item.unitPrice)}</td>
                      <td>{item.quantity ? item.quantity : 1}</td>
                      <td>{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              )}

              {pageIdx === itemPages.length - 1 && (
                <table className={styles['Bdc-totals']}>
                  <tbody>
                  <tr>
                    <th>Sous-total</th>
                    <td>{formatCurrency(totalPrice)}</td>
                  </tr>
                  <tr>
                    <th>Ajustement HT</th>
                    <td>
                      {isSignature ? (
                        formatCurrency(adjustment)
                      ) : (
                        <GKToggleableInput
                          value={adjustment?.toFixed(2)}
                          onChange={(value: string) =>
                            updateAdjustment(parseFloat(value) || 0)
                          }
                        />
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>TVA</th>
                    <td>{formatCurrency(totalVatAdjusted) || 0}</td>
                  </tr>
                  <tr>
                    <th>Total TTC</th>
                    <td>{formatCurrency(totalPriceWithVatAdjusted) || 0}</td>
                  </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* FOOTER */}
            {pageIdx === itemPages.length - 1 && itemPage.length < 11 && (
              <>
                <div className={styles['Bdc-middle']}>
                  <div className={styles['Bdc-comment']}>
                    <span>Remarques&nbsp;:</span>
                    {isSignature ? (
                      <div className={styles['Bdc-comment-area']}>
                        {comment}
                      </div>
                    ) : (
                      <textarea
                        rows={8}
                        value={comment}
                        onChange={(e) => updateComment(e)}
                      />
                    )}
                  </div>

                  <div className={styles['Bdc-signature']}>
                    <span>Signature</span>

                    {isSignature &&
                      project &&
                      (signatures[0] === true ? (
                        <p className={styles['Bdc-signed']}>{signature}</p>
                      ) : (
                        <div
                          ref={sign1Ref}
                          style={{ zIndex: '9999' }}
                          className={cx(
                            templateStyles['Template-signatureBox'],
                            styles['Bdc-signatureBox'],
                            styles['MandatGestion-signed'],
                            signatures[0] === 'pulse' && styles['pulse'],
                          )}
                          onClick={() => {
                            handleOpenModalSignHandler(0);
                          }}
                        >
                          <span>
                            Cliquez ici pour apposer
                            <br /> votre signature
                          </span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* MENTION */}
                <div className={styles['Bdc-mentions']}>
                  {!isKit && (
                    <div className={styles['Bdc-infos']}>
                      <p>
                        <span>Faculté de renonciation</span>&nbsp;: Le client a
                        la faculté de renoncer au contrat qu'il vient de
                        souscrire conformément aux conditions générales de
                        ventes en annexe.
                      </p>
                      <p>
                        <span>Délais d'installation</span>&nbsp;: La société
                        Greenkub s'engage à procéder à l'installation des
                        produits contractés dans les 24 semaines à compter de
                        la validation de l'Avant Projet Définitif (APD). Sauf
                        dans l'hypothèse d'un retard résultant d'un cas prévu
                        aux articles 7 et 14 des conditions générales de vente
                        ci-jointe
                      </p>
                    </div>
                  )}
                  {isKit ? (
                    <p>
                      <span>Conditions de règlement</span>&nbsp;: Dépôt de
                      garantie&nbsp;: 10% &bull; Production des
                      marchandises&nbsp;: 40% &bull; Expédition des
                      marchandises&nbsp;: 50% &bull; Pourcentages basés sur le
                      prix hors remise et ajustement. Toutes les remises et
                      ajustements seront déduits de la 3ème échéance.
                    </p>
                  ) : (
                    <p>
                      <span>Conditions de règlement</span>&nbsp;: Dépôt de
                      garantie&nbsp;: 10% &bull; Acceptation du projet par la
                      mairie&nbsp;: 40% &bull; Expédition des
                      marchandises&nbsp;: 40% &bull; Installation&nbsp;: 10%.
                      Pourcentages basés sur le prix hors remise et ajustement.
                      Toutes les remises et ajustements seront déduits de la
                      4ème et dernière échéance.
                    </p>
                  )}
                  <div
                    className={styles['Bdc-foot']}
                    style={{ textAlign: 'center' }}
                  >
                    {isKit ? (
                      <>
                        <p>
                          COVER GREEN SAS - RCS N° 901159418 au capital de 10
                          000 Euros - TVA N° FR93901159418
                        </p>
                        <p>
                          300 avenue de la Royale - 34160 Castries - Tel&nbsp;:
                          09 74 59 41 72
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          SAS Greenkub - RCS N° 798802377 au capital de 1 249
                          731,29 Euros - TVA N° FR27798802377
                        </p>
                        <p>
                          300 Avenue de la Royale 34160 Castries - France -
                          Tel&nbsp;: 09 74 59 41 72
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ))}

      {itemPages[itemPages.length - 1].length >= 11 && (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles[`page-${itemPages.length + 1}`],
          )}
        >
          {/* EN TETE DE PREMIER PAGE */}
          <div>
            {/* FOOTER */}
            <div
              className={cx(
                templateStyles['Template-middle'],
                styles['Amendment-middle'],
              )}
              style={{ marginTop: 0 }}
            >
              <div
                className={cx(
                  templateStyles['Template-comment'],
                  styles['Bdc-comment'],
                )}
              >
                <span>Remarques&nbsp;:</span>
                {isSignature ? (
                  <div className={styles['Bdc-comment-area']}>{comment}</div>
                ) : (
                  <textarea
                    rows={8}
                    value={comment}
                    onChange={(e) => setComment(e.currentTarget.value)}
                  />
                )}
              </div>

              <div className={styles['Bdc-signature']}>
                <span>Signature</span>

                {isSignature &&
                  project &&
                  (signatures[0] === true ? (
                    <p
                      className={cx(
                        templateStyles['Template-signed'],
                        styles['Bdc-signed'],
                      )}
                    >
                      {signature}
                    </p>
                  ) : (
                    <div
                      ref={sign1Ref}
                      style={{ zIndex: '9999' }}
                      className={cx(
                        templateStyles['Template-signatureBox'],
                        styles['Bdc-signatureBox'],
                        signatures[0] === 'pulse' && 'pulse',
                      )}
                      onClick={() => {
                        handleOpenModalSignHandler(0);
                      }}
                    >
                      <span>Cliquez ici pour apposer votre signature</span>
                    </div>
                  ))}
              </div>
            </div>

            {/* MENTION */}
            <div
              className={styles['Bdc-mentions']}
              style={{ position: 'unset' }}
            >
              <div className={styles['Bdc-infos']}>
                {!isKit && (
                  <>
                    <p>
                      <span>Faculté de renonciation</span>&nbsp;: Le client a la
                      faculté de renoncer au contrat qu'il vient de souscrire
                      conformément aux conditions générales de ventes en annexe.
                    </p>
                    <p>
                      <span>Délais d'installation</span>&nbsp;: La société
                      Greenkub s'engage à procéder à l'installation des produits
                      contractés dans les 24 semaines à compter de la validation
                      de l'Avant Projet Définitif (APD). Sauf dans l'hypothèse
                      d'un retard résultant d'un cas prévu aux articles 7 et 14
                      des conditions générales de vente ci-jointe
                    </p>
                  </>
                )}
                {isKit ? (
                  <p>
                    <span>Conditions de règlement</span>&nbsp;: Dépôt de
                    garantie&nbsp;: 10% &bull; Production des
                    marchandises&nbsp;: 40% &bull; Expédition des
                    marchandises&nbsp;: 50% &bull; Pourcentages basés sur le
                    prix hors remise et ajustement. Toutes les remises et
                    ajustements seront déduits de la 3ème échéance.
                  </p>
                ) : (
                  <p>
                    <span>Conditions de règlement</span>&nbsp;: Dépôt de
                    garantie&nbsp;: 10% &bull; Acceptation du projet par la
                    mairie&nbsp;: 40% &bull; Expédition des marchandises&nbsp;:
                    40% &bull; Installation&nbsp;: 10%. Pourcentages basés sur
                    le prix hors remise et ajustement. Toutes les remises et
                    ajustements seront déduits de la 4ème et dernière échéance.
                  </p>
                )}
              </div>
              <div
                className={styles['Bdc-foot']}
                style={{ textAlign: 'center' }}
              >
                {isKit ? (
                  <>
                    <p>
                      COVER GREEN SAS - RCS N° 901159418 au capital de 10 000
                      Euros - TVA N° FR93901159418
                    </p>
                    <p>
                      300 avenue de la Royale - 34160 Castries - Tel&nbsp;: 09
                      74 59 41 72
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      SAS Greenkub - RCS N° 798802377 au capital de 1 249 731,29
                      Euros - TVA N° FR27798802377
                    </p>
                    <p>
                      300 Avenue de la Royale 34160 Castries - France -
                      Tel&nbsp;: 09 74 59 41 72
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {modelNames.map((modelName) => {
        const images = modelImages[modelName];
        const fallbackFragments = renderOldGalleryForModel(modelName);
        return (
        <Fragment key={modelName}>
          {loadingModels[modelName] && (
            <div
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void']
              )}
            >
              <div style={{ padding: '24px', textAlign: 'center' }}>
                Chargement des descriptifs techniques pour {modelName}...
              </div>
            </div>
          )}
          {errorModels[modelName] && (
            fallbackFragments.length > 0 ? (
              <>{fallbackFragments}</>
            ) : (
              <div
                className={cx(
                  mainStyles['LandingPage-page'],
                  templateStyles['Template'],
                  styles['page'],
                  styles['void']
                )}
              >
                <div style={{ padding: '24px', color: 'red', textAlign: 'center' }}>
                  Erreur de chargement pour {modelName}: {errorModels[modelName]}
                </div>
              </div>
            )
          )}
          {images?.map((src, i) => (
            <div
              key={`${modelName}-${i}`}
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void']
              )}
            >
              <img
                className={styles['full-picture']}
                src={src}
                alt={`${modelName}-descriptif-${i + 1}`}
              />
            </div>
          ))}
        </Fragment>
        );
      })}

      {/* DOCUMENT MENTION GENERALES */}
      {outroImages.length > 0 ? (
        outroImages.map((imageUrl, index) => (
          <div
            key={`outro-${index}`}
            className={cx(
              mainStyles['LandingPage-page'],
              templateStyles['Template'],
              styles['page'],
              styles['void'],
            )}
          >
            <img
              className={styles['full-picture']}
              src={imageUrl}
              alt={`outro-${index + 1}`}
            />
          </div>
        ))
      ) : (
        !isKit && (
          <>
            <div
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void'],
              )}
            >
              <img
                className={styles['full-picture']}
                src={bdcOutro1}
                alt="bdcOutro1"
              />
            </div>
            <div
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void'],
              )}
            >
              <img
                className={styles['full-picture']}
                src={bdcOutro2}
                alt="bdcOutro2"
              />
            </div>
          </>
        )
      )}
      {/* Dynamic CGV Images */}
      {cgvImages.length > 0 ? (
        cgvImages.map((imageUrl, index) => {
          const isLastImage = index === cgvImages.length - 1;
          return (
            <div
              key={`cgv-${index}`}
              className={cx(
                mainStyles['LandingPage-page'],
                templateStyles['Template'],
                styles['page'],
                styles['void'],
              )}
            >
              <img
                className={styles['full-picture']}
                src={imageUrl}
                alt={`cgv-${index + 1}`}
              />
              {/* Add signature section to the last CGV image */}
              {isLastImage && (
                <div className={styles['Bdc-bdc-signature']}>
                  {isSignature &&
                    project &&
                    (signatures[1] === true ? (
                      <p className={styles['Bdc-signed']}>{signature}</p>
                    ) : (
                      <div
                        ref={sign2Ref}
                        style={{ zIndex: '9999' }}
                        className={cx(
                          templateStyles['Template-signatureBox'],
                          styles['MandatGestion-signed'],
                          styles['Bdc-signatureBox'],
                          signatures[1] === 'pulse' && styles['pulse'],
                        )}
                        onClick={() => {
                          handleOpenModalSignHandler(1);
                        }}
                      >
                        <span>
                          Cliquez ici pour apposer
                          <br /> votre signature
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })
      ) : loadingCgv ? (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles['void'],
          )}
        >
          <p>Chargement des CGV...</p>
        </div>
      ) : errorCgv ? (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles['void'],
          )}
        >
          <p>Erreur lors du chargement des CGV: {errorCgv}</p>
        </div>
      ) : (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles['void'],
          )}
        >
          <p>Aucune CGV disponible</p>
        </div>
      )}

      {/* BORDEREAU DE RETRACTION */}
      {retractationImages.length > 0 ? (
        retractationImages.map((retractUrl, index) => (
          <div
            key={`retract-${index}`}
            className={cx(
              mainStyles['LandingPage-page'],
              templateStyles['Template'],
              styles['page'],
              styles['void'],
            )}
          >
            <img
              className={styles['full-picture']}
              src={retractUrl}
              alt={`retractation-${index + 1}`}
            />
          </div>
        ))
      ) : (
        <div
          className={cx(
            mainStyles['LandingPage-page'],
            templateStyles['Template'],
            styles['page'],
            styles['void'],
          )}
        >
          <img
            className={styles['full-picture']}
            src={bdcRetract}
            alt="bdcRetract"
          />
        </div>
      )}
    </div>
  );
};

export default Bdc;
